import {
  Component,
  computed,
  effect,
  EffectRef,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  input,
  output
} from '@angular/core';
import {ThemeCustomizerService} from '../../../common/theme-customizer/theme-customizer.service';
import {CartService} from '../../../core/services/cart.service';
import {RouterLink} from '@angular/router';
import {ApiService} from '../../../core/services/api.service';
import {CurrencyPipe, NgForOf, NgIf, NgOptimizedImage} from '@angular/common';
import {ReactiveFormsModule} from '@angular/forms';
import {createLogger} from '../../../shared/helpers/logger/logger';
import {GuestCartService} from "../../../shared/helpers/guest-cart/guest-cart.service";
import {UserService} from "../../../core/services/user.service";
import {CartManagerService} from "../../../core/services/cart-manager.service";
import Swal from "sweetalert2";
import {Workshop} from "../../../core/models/workshop.model";
import {Product} from "../../../core/models/product.model";
import {Course} from "../../../core/models/course.model";
import {TimerPipe} from "../../../core/pipes/timer.pipe";
import {AnalyticsGaService} from "../../../shared/helpers/analytics/analytics-ga.service";

const logger = createLogger('CartPageComponent');

@Component({
    standalone: true,
    selector: 'app-cart-page',
    imports: [NgIf, NgForOf, ReactiveFormsModule, NgOptimizedImage, CurrencyPipe, RouterLink, TimerPipe],
    templateUrl: './cart-page.component.html',
    styleUrls: ['./cart-page.component.scss']
})
export class CartPageComponent implements OnInit, OnDestroy {

    readonly isSideCartOpen = input(false);
    readonly toggleSideCart = output<void>();
    cart = this.cartService.currentCart;
    isLoading = signal(true);
    isLoggedIn = computed(() => this.userService.isLoggedIn());
    public timers = new Map<string, Signal<number>>();
    private loginEffectCleanup?: EffectRef;

    constructor(
        public themeService: ThemeCustomizerService,
        public cartService: CartService,
        public apiService: ApiService,
        private guestService: GuestCartService,
        private userService: UserService,
        private cartManagerService: CartManagerService,
        private analyticsService: AnalyticsGaService
    ) {
        this.loginEffectCleanup = effect(async () => {
            if (this.isLoggedIn()) {
                await this.loadCartDetails();
            }
        });
        effect(() => {
            const cart = this.cart();
            if (cart) {
                cart.cartItems.forEach(item => {
                    if (item.entity_type === 'Event' && !item.timer) {
                        this.startTimer(item);
                    }
                });
                this.analyticsService.viewCart(cart);
            }
        });
    }

    async ngOnInit(): Promise<void> {
        await this.loadCartDetails();
        this.isLoading.set(false);
    }

    ngOnDestroy() {
        this.loginEffectCleanup?.destroy();
    }

    async loadCartDetails(): Promise<void> {
        try {
            const params: any = {expand: 'cartItems,cartItems.entity,cartItems.variant'};
            if (!this.isLoggedIn()) params.guest_token = this.guestService.getGuestToken();
            await this.cartService.getCart(params);
        } catch (error) {
            logger.error('Error loading cart details:', error);
        }
    }

    emitToggleSideCart() {
        // TODO: The 'emit' function requires a mandatory void argument
        this.toggleSideCart.emit();
    }

    getTimerValue(uuid: string): number | undefined {
        const timerSignal = this.timers.get(uuid);
        return timerSignal ? timerSignal() : undefined;
    }

    async updateQuantity(item: any, change: number): Promise<void> {
        if (item.entity_type === 'Course' && change > 0) {
            await Swal.fire({
                title: 'Cannot Increase Quantity',
                text: 'You can only purchase one of each course.',
                icon: 'info',
                confirmButtonText: 'OK'
            });
            return;
        }

        try {
            const newQuantity = item.quantity + change;
            if (newQuantity <= 0) {
                await this.removeItem(item.uuid);
            } else {
                await this.cartService.updateCart(item.uuid, newQuantity);
                const analyticsType = item.entity_type === 'Variant' ? 'Product' : item.entity_type;
                if (change > 0) {
                    this.analyticsService.addToCart(item.entity, analyticsType as 'Product' | 'Course' | 'Event', change);
                } else {
                    this.analyticsService.removeFromCart(item.entity, analyticsType as 'Product' | 'Course' | 'Event', -change);
                }
            }
        } catch (error) {
            logger.error('Error updating quantity:', error);
        }
    }

    async removeItem(itemUuid: string): Promise<void> {
        const item = this.cart()?.cartItems?.find(i => i.uuid === itemUuid);
        if (item) {
            const analyticsType = item.entity_type === 'Variant' ? 'Product' : item.entity_type;
            this.analyticsService.removeFromCart(item.entity, analyticsType as 'Product' | 'Course' | 'Event', item.quantity);
        }
        try {
            await this.cartService.deleteCartItem(itemUuid);
            this.timers.delete(itemUuid);
        } catch (error) {
            logger.error('Error removing item:', error);
        }
    }

    getItemPrice(item: any): number {
        return item.price ?? (item.entity_type === 'Product'
            ? (item.variant?.price ?? item.entity.price ?? 0)
            : item.entity.cost ?? item.entity.price ?? 0);
    }

    getItemDisplayPrice(item: any): string {
        if (item.is_free) {
            return 'Free';
        }
        const totalPrice = this.getItemPrice(item) * item.quantity;
        return totalPrice.toLocaleString('en-IN', {style: 'currency', currency: 'INR'});
    }

    getEntityImage(item: any): string {
        if (item.entity_type === 'Product') {
            return (item.entity as Product).media_link || 'assets/images/default-product.jpg';
        } else if (item.entity_type === 'Course') {
            return (item.entity as Course).thumbnail || 'assets/images/default-course.jpg';
        } else if (item.entity_type === 'Event') {
            return (item.entity as Workshop).display_image || 'assets/images/default-event.jpg';
        }
        return 'assets/images/default.jpg';
    }

    getEntityName(item: any): string {
        if (item.entity_type === 'Product') {
            return (item.entity as Product).name || '';
        } else if (item.entity_type === 'Course') {
            return (item.entity as Course).title || '';
        } else if (item.entity_type === 'Event') {
            return (item.entity as Workshop).title || '';
        }
        return '';
    }

    startTimer(item: any) {
        if (item.entity_type === 'Event') {
            const updatedAtUTC = new Date(item.updated_at + 'Z');
            if (isNaN(updatedAtUTC.getTime())) {
                logger.error('Invalid updated_at date:', item.updated_at);
                return;
            }

            const totalDuration = 15 * 60 * 1000; // 15 minutes in milliseconds
            const nowUTC = new Date().getTime();
            const elapsed = nowUTC - updatedAtUTC.getTime();
            let remainingTimeMs = totalDuration - elapsed;

            if (remainingTimeMs <= 0) {
                this.removeItem(item.uuid);
                return;
            }

            const timerSignal = signal(Math.floor(remainingTimeMs / 1000));
            this.timers.set(item.uuid, timerSignal);

            const interval = setInterval(() => {
                remainingTimeMs -= 1000;
                if (remainingTimeMs <= 0) {
                    clearInterval(interval);
                    this.removeItem(item.uuid);
                    this.timers.delete(item.uuid);
                } else {
                    timerSignal.set(Math.floor(remainingTimeMs / 1000));
                }
            }, 1000);
        }
    }
}
